import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Paths from "@/constants/paths";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { roleCheck } from "@/lib/roleCheck";
import { logout } from "@/stores/authStore";
import { useGetExpeditionCountAll } from "@/api/expedition.api";
import { useGetNotificationsCountBo } from "@/api/notifications.api";
import { cn } from "@/lib/utils";
import {
  NotificationSheetContent,
  DEFAULT_NOTIFICATION_KEYS,
  URGENT_KEYS_PARAM,
} from "@/components/backOffice/notifications.modal";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Search,
  Clock,
  Box,
  ClipboardClock,
  Truck,
  Users,
  Euro,
  Settings,
  LogOut,
  Building2,
  BottleWine,
  Tag,
  ChevronDown,
  ScrollText,
  Bell,
  Bug,
  PackageCheck,
} from "lucide-react";
import { Badge } from "../ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "../ui/sidebar";

type NavbarBoProps = {
  role?: string;
};

type NavItemProps = {
  path: string;
  icon: React.ReactNode;
  title: string;
  badgeCount?: number;
  // Overrides the default (red/destructive) styling of the primary badge, e.g. "bg-blue-500 text-white hover:bg-blue-500".
  badgeColorClass?: string;
  // Optional second badge (e.g. a breakdown of `badgeCount`). Only ever shown alongside the primary badge in the
  // expanded sidebar — the collapsed/minified view only ever displays `badgeCount`.
  secondaryBadgeCount?: number;
  secondaryBadgeColorClass?: string;
  isNew?: boolean;
};

const NavItem = ({
  path,
  icon,
  title,
  badgeCount,
  badgeColorClass,
  secondaryBadgeCount,
  secondaryBadgeColorClass,
  isNew,
}: NavItemProps) => {
  const { setOpenMobile, isMobile } = useSidebar();
  const { t } = useTranslation("boNavigation");

  const hasPrimaryBadge = !!badgeCount && badgeCount > 0;
  const hasSecondaryBadge = !!secondaryBadgeCount && secondaryBadgeCount > 0;

  return (
    <SidebarMenuItem>
      <NavLink to={path} onClick={() => isMobile && setOpenMobile(false)}>
        {({ isActive }) => (
          <SidebarMenuButton
            isActive={isActive}
            tooltip={title}
            size="lg"
            className="relative gap-3 border-l-4 border-transparent text-gray-600 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!overflow-visible hover:text-blue-600 data-[active=true]:border-blue-600 data-[active=true]:bg-blue-100 data-[active=true]:font-semibold data-[active=true]:text-blue-700 [&>svg]:!size-5 data-[active=true]:[&>svg]:text-blue-600"
          >
            {icon}
            <span className="truncate text-sm group-data-[collapsible=icon]:hidden">
              {title}
              {isNew && (
                <span className="ml-1 text-[10px] font-semibold text-red-500">
                  {t("sidebar.newBadge")}
                </span>
              )}
            </span>
            {hasPrimaryBadge || hasSecondaryBadge ? (
              <>
                <div className="ml-auto flex items-center gap-1 group-data-[collapsible=icon]:hidden">
                  {hasPrimaryBadge && (
                    <Badge
                      variant={badgeColorClass ? undefined : "destructive"}
                      className={cn("h-4 min-w-4 px-1 text-[10px]", badgeColorClass)}
                    >
                      {badgeCount}
                    </Badge>
                  )}
                  {hasSecondaryBadge && (
                    <Badge
                      variant={secondaryBadgeColorClass ? undefined : "destructive"}
                      className={cn("h-4 min-w-4 px-1 text-[10px]", secondaryBadgeColorClass)}
                    >
                      {secondaryBadgeCount}
                    </Badge>
                  )}
                </div>
                {hasPrimaryBadge && (
                  <Badge
                    variant={badgeColorClass ? undefined : "destructive"}
                    className={cn(
                      "pointer-events-none absolute top-0.5 right-0.5 z-50 hidden h-4 min-w-4 translate-x-1/2 px-1 text-[10px] group-data-[collapsible=icon]:inline-flex",
                      badgeColorClass,
                    )}
                  >
                    {badgeCount}
                  </Badge>
                )}
              </>
            ) : null}
          </SidebarMenuButton>
        )}
      </NavLink>
    </SidebarMenuItem>
  );
};

type NavItemCollapsibleProps = {
  icon: React.ReactNode;
  title: string;
  items: { title: string; path: string }[];
};

const NavItemCollapsible = ({ icon, title, items }: NavItemCollapsibleProps) => {
  const { state, isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const location = useLocation();

  const isOnThisSection = items.some((item) => location.pathname === item.path);

  const [open, setOpen] = useState(isOnThisSection);

  useEffect(() => {
    if (isOnThisSection) setOpen(true);
  }, [isOnThisSection]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={title}
            size="lg"
            className="relative gap-3 text-gray-600 group-data-[collapsible=icon]:justify-center hover:text-blue-600 [&>svg]:!size-5"
            onClick={(e) => {
              if (state === "collapsed") {
                e.preventDefault();
                toggleSidebar();
                setOpen(true);
              }
            }}
          >
            {icon}
            <span className="truncate text-sm group-data-[collapsible=icon]:hidden">{title}</span>
            <ChevronDown className="ml-auto h-4 w-4 shrink-0 transition-transform duration-200 group-data-[collapsible=icon]:hidden group-data-[state=open]/collapsible:rotate-180" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {items.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <SidebarMenuSubItem key={item.path}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={isActive}
                    className="border-l-4 border-transparent text-gray-600 hover:bg-blue-50 hover:text-blue-600 data-[active=true]:border-blue-600 data-[active=true]:bg-blue-100 data-[active=true]:font-semibold data-[active=true]:text-blue-700"
                  >
                    <NavLink to={item.path} onClick={() => isMobile && setOpenMobile(false)}>
                      {item.title}
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

// Rendered only for the roles allowed to read BO notifications — keeping the hook inside this component
// means PACKER (which has no access to the notifications endpoint) never triggers the query.
const NotificationsNavItem = () => {
  const { data: notificationsCount } = useGetNotificationsCountBo({
    logKeys: DEFAULT_NOTIFICATION_KEYS,
    urgentLogKeys: URGENT_KEYS_PARAM,
  });
  const { t } = useTranslation("boNavigation");

  return (
    <SidebarMenuItem>
      <Sheet>
        <SheetTrigger asChild>
          <SidebarMenuButton
            tooltip={t("sidebar.notifications")}
            size="lg"
            className="relative gap-3 text-yellow-600 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!overflow-visible hover:text-blue-600 [&>svg]:!size-5"
          >
            <Bell />
            <span className="truncate text-sm group-data-[collapsible=icon]:hidden">
              {t("sidebar.notifications")}
            </span>
            {notificationsCount &&
            (notificationsCount.urgent > 0 || notificationsCount.normal > 0) ? (
              <>
                <div className="pointer-events-none ml-auto flex gap-1 group-data-[collapsible=icon]:hidden">
                  {notificationsCount.urgent > 0 && (
                    <Badge variant="destructive" className="h-4 min-w-4 px-1 text-[10px]">
                      {notificationsCount.urgent > 99 ? "99+" : notificationsCount.urgent}
                    </Badge>
                  )}
                  {notificationsCount.normal > 0 && (
                    <Badge className="h-4 min-w-4 bg-blue-500 px-1 text-[10px] hover:bg-blue-500">
                      {notificationsCount.normal > 99 ? "99+" : notificationsCount.normal}
                    </Badge>
                  )}
                </div>
                <Badge
                  variant={notificationsCount.urgent > 0 ? "destructive" : undefined}
                  className={`pointer-events-none absolute top-0.5 right-0.5 z-50 hidden h-4 min-w-4 translate-x-1/2 px-1 text-[10px] group-data-[collapsible=icon]:inline-flex${notificationsCount.urgent === 0 ? "bg-blue-500 hover:bg-blue-500" : ""}`}
                >
                  {notificationsCount.urgent + notificationsCount.normal > 99
                    ? "99+"
                    : notificationsCount.urgent + notificationsCount.normal}
                </Badge>
              </>
            ) : null}
          </SidebarMenuButton>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="flex w-96 flex-col p-0 [&>button:first-of-type]:hidden"
        >
          <NotificationSheetContent
            notificationsCount={notificationsCount || { normal: 0, urgent: 0 }}
          />
        </SheetContent>
      </Sheet>
    </SidebarMenuItem>
  );
};

// Rendered only for the roles allowed to read the expedition counters — keeping the hooks inside this
// component means DRIVER and PACKER (forbidden on those endpoints) never trigger the queries.
const ExpeditionsNavGroup = ({ role }: { role?: string }) => {
  const { data: expeditionCountAll } = useGetExpeditionCountAll();
  const pendingCustomQuotationCount = expeditionCountAll?.pendingCustomQuotationsCount;
  const pendingExpeditionsCount = expeditionCountAll?.pendingExpeditionsCount;
  const validatedExpeditionsCount = expeditionCountAll?.validatedExpeditionsCount;
  const blockedExpeditionsCount = expeditionCountAll?.blockedExpeditionsCount;
  const latePendingExpeditionsCount = expeditionCountAll?.latePendingExpeditionsCount;
  const { t } = useTranslation("boNavigation");

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          <NotificationsNavItem />
          {roleCheck(["ADMIN", "SUPERADMIN", "MEMBER"], role) && (
            <SidebarSeparator className="my-2" />
          )}

          <>
            {roleCheck(["SUPERADMIN", "ADMIN"], role) && (
              <NavItem
                path={Paths.bo.customQuotationsBox}
                icon={<Clock />}
                title={t("sidebar.customQuotations")}
                badgeCount={pendingCustomQuotationCount}
              />
            )}
            {roleCheck(["ADMIN", "SUPERADMIN", "MEMBER"], role) && (
              <NavItem path={Paths.bo.ratings} icon={<Tag />} title={t("sidebar.ratings")} />
            )}
            {roleCheck(["ADMIN", "SUPERADMIN", "MEMBER"], role) && (
              <>
                <SidebarSeparator className="my-2" />
                <NavItem
                  path={Paths.bo.expeditionsBox}
                  icon={<Box />}
                  title={t("sidebar.expeditionsBox")}
                  badgeCount={pendingExpeditionsCount}
                  badgeColorClass="bg-blue-500 text-white hover:bg-blue-500"
                  secondaryBadgeCount={latePendingExpeditionsCount}
                />
                <NavItem
                  path={Paths.bo.validatedExpeditionsV2}
                  icon={<ClipboardClock />}
                  title={t("sidebar.validatedExpeditions")}
                  badgeCount={validatedExpeditionsCount}
                  badgeColorClass="bg-blue-500 text-white hover:bg-blue-500"
                />
              </>
            )}
            {roleCheck(["ADMIN", "SUPERADMIN", "MEMBER"], role) && (
              <NavItem
                path={Paths.bo.blockedExpeditionsV2}
                icon={<Bug />}
                title={t("sidebar.blockedExpeditions")}
                badgeCount={blockedExpeditionsCount}
                badgeColorClass="bg-orange-500 text-white hover:bg-orange-500"
              />
            )}
          </>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

export const NavbarBo = ({ role }: NavbarBoProps) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { t } = useTranslation("boNavigation");

  // DRIVER and PACKER are each limited to a single page (Collecte / Conditionnement): no search, no
  // notifications and no expedition counters — the matching endpoints are forbidden to them anyway.
  const hasFullBoNav = roleCheck(["ADMIN", "SUPERADMIN", "MEMBER", "ACCOUNTANT"], role);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["cotationsCount"] });
  }, [location, queryClient]);

  return (
    <Sidebar collapsible="icon" className="bg-white">
      <SidebarHeader className="flex flex-row items-center justify-between gap-2 overflow-hidden py-3 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0">
        <img
          src="/images/pelican-logo.png"
          alt="Pelican Air Services"
          className="h-14 w-14 object-contain pl-2 group-data-[collapsible=icon]:hidden"
        />
        <SidebarTrigger className="size-8 shrink-0 text-gray-600 hover:text-blue-600" />
      </SidebarHeader>

      <SidebarContent className="overflow-x-hidden">
        {hasFullBoNav && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {" "}
                <NavItem
                  path={Paths.bo.expeditionsHistoryV2}
                  icon={<Search />}
                  title={t("sidebar.search")}
                />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {hasFullBoNav && <ExpeditionsNavGroup role={role} />}

        {roleCheck(["ADMIN", "SUPERADMIN", "DRIVER", "PACKER", "MEMBER"], role) && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {roleCheck(["ADMIN", "SUPERADMIN", "PACKER", "MEMBER"], role) && (
                  <>
                    <SidebarSeparator className="my-1" />
                    <NavItem
                      path={Paths.bo.packaging}
                      icon={<PackageCheck />}
                      title={t("sidebar.packaging")}
                      isNew
                    />
                  </>
                )}
                {roleCheck(["ADMIN", "SUPERADMIN", "DRIVER"], role) && (
                  <>
                    <NavItem
                      path={Paths.bo.collection}
                      icon={<Truck />}
                      title={t("sidebar.collection")}
                    />
                    <SidebarSeparator className="my-1" />
                  </>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {roleCheck(["ADMIN", "SUPERADMIN", "ACCOUNTANT", "MEMBER"], role) && (
                <NavItem path={Paths.bo.clients} icon={<Users />} title={t("sidebar.clients")} />
              )}
              {roleCheck(["SUPERADMIN", "ADMIN", "MEMBER"], role) && (
                <NavItem
                  path={Paths.bo.importer}
                  icon={<Building2 />}
                  title={t("sidebar.importers")}
                />
              )}
              {roleCheck(["SUPERADMIN", "ADMIN", "MEMBER"], role) && (
                <NavItem
                  path={Paths.bo.products}
                  icon={<BottleWine />}
                  title={t("sidebar.products")}
                />
              )}
              {roleCheck(["SUPERADMIN", "ACCOUNTANT"], role) && (
                <>
                  <SidebarSeparator className="my-2" />
                  <NavItem path={Paths.bo.invoices} icon={<Euro />} title={t("sidebar.invoices")} />
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {roleCheck(["SUPERADMIN", "ADMIN"], role) && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <NavItemCollapsible
                  icon={<Settings />}
                  title={t("sidebar.parameters")}
                  items={[
                    { title: t("sidebar.parametersCustoms"), path: Paths.bo.parametersCustoms },
                    ...(roleCheck(["SUPERADMIN"], role)
                      ? [
                          {
                            title: t("sidebar.parametersTariffs"),
                            path: Paths.bo.parametersTarifs,
                          },
                          {
                            title: t("sidebar.parametersOptions"),
                            path: Paths.bo.parametersOptions,
                          },
                          { title: t("sidebar.parametersUsers"), path: Paths.bo.parametersUsers },
                          {
                            title: t("sidebar.parametersRegister"),
                            path: Paths.bo.parametersRegister,
                          },
                        ]
                      : []),
                  ]}
                />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {roleCheck(["SUPERADMIN"], role) && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <NavItem path={Paths.bo.logs} icon={<ScrollText />} title={t("sidebar.logs")} />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => logout()}
              tooltip={t("sidebar.logout")}
              size="lg"
              className="gap-3 text-gray-600 group-data-[collapsible=icon]:justify-center hover:text-blue-600 [&>svg]:!size-5"
            >
              <LogOut />
              <span className="text-sm group-data-[collapsible=icon]:hidden">
                {t("sidebar.logout")}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
